import React, { useState, useRef, useEffect } from 'react';
import { LogOut, Moon } from 'lucide-react';
import { useTheme } from './ThemeProvider';

interface UserAvatarDropdownProps {
  email: string;
  name?: string;
  onSignOut: () => void;
}

function getInitials(name: string): string {
  const words = name.trim().split(/\s+/);
  if (words.length >= 2) {
    return (words[0][0] + words[1][0]).toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
}

export default function UserAvatarDropdown({ email, name = 'Admin Account1', onSignOut }: UserAvatarDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { theme, toggleTheme } = useTheme();

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const initials = getInitials(name);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center text-blue-500 font-medium text-sm hover:bg-blue-100 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-200"
      >
        {initials}
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-64 bg-card rounded-lg shadow-lg border border-border py-3 z-50">
          <div className="px-4 pb-3 border-b border-border">
            <p className="font-semibold text-foreground">{name}</p>
            <p className="text-sm text-muted-foreground truncate">{email}</p>
          </div>
          
          {/* Dark Mode Toggle */}
          <button
            onClick={toggleTheme}
            className="w-full flex items-center justify-between px-4 py-2.5 text-foreground hover:bg-muted transition-colors"
          >
            <div className="flex items-center gap-2">
              <Moon className="w-4 h-4" />
              <span>Dark Mode</span>
            </div>
            <div className={`w-9 h-5 rounded-full transition-colors ${theme === 'dark' ? 'bg-primary' : 'bg-muted-foreground/30'}`}>
              <div className={`w-4 h-4 rounded-full bg-background shadow-sm transform transition-transform mt-0.5 ${theme === 'dark' ? 'translate-x-4.5 ml-0.5' : 'translate-x-0.5'}`} />
            </div>
          </button>
          
          <div className="border-t border-border mt-1 pt-1">
            <button
              onClick={() => {
                setIsOpen(false);
                onSignOut();
              }}
              className="w-full flex items-center gap-2 px-4 py-2 text-destructive hover:bg-muted transition-colors text-left"
            >
              <LogOut className="w-4 h-4" />
              <span>Sign out</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
